import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ReportBlock, ReportTable, ReportTableColumn } from '../shared/models/report-block.model';
import { Popup } from '../../../core/shared/components/popup/popup';
import { ToastService } from '../../../core/shared/services/toast.service';
import { CraditService } from '../shared/services/cradit.service';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { RichTextEditor } from '../shared/components/rich-text-editor/rich-text-editor';
import { SectionDragHandle } from '../shared/components/section-drag-handle/section-drag-handle';
import { RichTextPipe } from '../shared/utils/rich-text.pipe';
import { TableCellPipe } from '../shared/utils/table-cell.pipe';
import { richTextForSave } from '../shared/utils/rich-text.util';
import { moveVisibleItem } from '../shared/utils/section-reorder.util';


@Component({
  selector: 'app-borrow-perposal-review',
  imports: [
    CommonModule,
    FormsModule,
    Popup,
    TranslatePipe,
    RichTextEditor,
    RichTextPipe,
    TableCellPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SectionDragHandle,
  ],
  templateUrl: './borrow-perposal-review.html',
  styleUrl: './borrow-perposal-review.css',
})
export class BorrowPerposalReview
  implements OnInit, OnChanges, OnDestroy {

  @Input() sectionId = '';
  @Input() editable = false;
  @Input() refGrp = 28;
  @Input() refValSeq!: number;

  blocks: ReportBlock[] = [];

  onboardingId = 0;

  crdtPrpslSeq: number | null = null;
  crdtPrpslBlkSeq: number | null = null;

  loading = false;
  saving = false;

  errorMessage = '';
  successMessage = '';

  showDeletePopup = false;
  deleteMessage = '';
  private pendingDeleteIndex: number | null = null;

  private saveSub?: Subscription;
  private seqSub?: Subscription;
  private initialized = false;

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.onboardingId =
      this.getOnboardingSequence();

    this.crdtPrpslSeq =
      this.creditService.getCrdtPrpslSeq();

    this.initialized = true;

    this.seqSub =
      this.creditService.crdtPrpslSeq$
        .subscribe((sequence) => {
          const normalizedSequence =
            Number(sequence ?? 0);

          if (normalizedSequence <= 0) {
            return;
          }

          const sequenceChanged =
            normalizedSequence !==
            Number(this.crdtPrpslSeq);

          this.crdtPrpslSeq =
            normalizedSequence;

          if (
            sequenceChanged ||
            this.blocks.length === 0
          ) {
            this.loadSection();
          }
        });

    this.saveSub =
      this.creditService.saveSectionTrigger$
        .subscribe((sectionId) => {
          if (
            sectionId === this.sectionId
          ) {
            this.saveSection();
          }
        });

    if (
      this.crdtPrpslSeq &&
      this.refValSeq
    ) {
      this.loadSection();
    }
  }

  ngOnChanges(
    changes: SimpleChanges,
  ): void {
    const sectionChanged =
      changes['sectionId'] ||
      changes['refValSeq'] ||
      changes['refGrp'];

    if (
      !this.initialized ||
      !sectionChanged
    ) {
      return;
    }

    this.blocks = [];
    this.crdtPrpslBlkSeq = null;

    this.errorMessage = '';
    this.successMessage = '';

    this.loadSection();
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
    this.seqSub?.unsubscribe();
  }

  get activeBlocks(): ReportBlock[] {
    return this.blocks.filter(
      (block) =>
        Number(block.delFlg ?? 0) !== 1,
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByBlock(
    _index: number,
    block: ReportBlock,
  ): ReportBlock {
    // Object identity: stable while the user types (keying on `block.title`
    // recreated the block's DOM — and its focused input — on every keystroke)
    // and it moves with the block when blocks are reordered by drag and drop.
    return block;
  }

  /* ====================================================== */
  /* LOAD SECTION */
  /* ====================================================== */

  loadSection(): void {
    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (
      proposalSequence <= 0 ||
      Number(this.refValSeq) <= 0
    ) {
      this.blocks = [];

      return;
    }

    const requestedSectionId =
      this.sectionId;

    const requestedRefValSeq =
      Number(this.refValSeq);

    this.loading = true;
    this.errorMessage = '';

    this.creditService
      .getCrdtBlck(
        proposalSequence,
        [requestedRefValSeq],
      )
      .subscribe({
        next: (response: any) => {
          this.loading = false;

          if (
            requestedSectionId !==
              this.sectionId ||
            requestedRefValSeq !==
              Number(this.refValSeq)
          ) {
            return;
          }

          const responseRows =
            this.extractResponseRows(
              response,
            );

          const row =
            responseRows.find(
              (item: any) =>
                Number(
                  item?.refValSeq,
                ) ===
                requestedRefValSeq &&
                Number(
                  item?.delFlg ?? 0,
                ) !== 1,
            ) ??
            responseRows[0] ??
            null;

          if (!row) {
            this.blocks = [];
            this.crdtPrpslBlkSeq =
              null;

            return;
          }

          this.crdtPrpslBlkSeq =
            this.toNullableNumber(
              row?.crdtPrpslBlkSeq,
            );

          const savedBlocks =
            this.extractSavedBlocks(
              row?.jsonData,
            );

          this.blocks =
            savedBlocks.map(
              (
                block: ReportBlock,
              ): ReportBlock => ({
                type:
                  this.normalizeBlockType(
                    block?.type,
                  ),

                title:
                  String(
                    block?.title ?? '',
                  ),

                content:
                  String(
                    block?.content ?? '',
                  ),

                expanded:
                  false,

                delFlg:
                  Number(
                    block?.delFlg ?? 0,
                  ),

                table:
                  block?.type === 'table'
                    ? this.normalizeTable(
                        block?.table,
                      )
                    : undefined,
              }),
            );
        },

        error: (error) => {
          this.loading = false;
          this.blocks = [];

          this.errorMessage =
            error?.error?.message ??
            this.translate.instant('PROPOSAL_REVIEW.LOAD_FAILED');

          console.error(
            'Load rich section error:',
            error,
          );
        },
      });
  }

  private extractResponseRows(
    response: any,
  ): any[] {
    const possibleLists = [
      response,
      response?.data,
      response?.content,
      response?.data?.content,
      response?.blockList,
      response?.data?.blockList,
      response?.crdtPrpslBlkList,
      response?.data?.crdtPrpslBlkList,
    ];

    const matchedList =
      possibleLists.find(
        (value: unknown) =>
          Array.isArray(value),
      );

    return Array.isArray(matchedList)
      ? matchedList
      : [];
  }

  private extractSavedBlocks(
    jsonData: unknown,
  ): ReportBlock[] {
    const normalizedJsonData =
      this.normalizeJsonData(
        jsonData,
      );

    const firstItem =
      normalizedJsonData[0];

    if (
      !firstItem ||
      !Array.isArray(
        firstItem?.blocks,
      )
    ) {
      return [];
    }

    return firstItem.blocks;
  }

  private normalizeJsonData(
    value: unknown,
  ): Array<{
    blocks?: ReportBlock[];
  }> {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return [];
    }

    if (Array.isArray(value)) {
      return value as Array<{
        blocks?: ReportBlock[];
      }>;
    }

    if (
      typeof value === 'object'
    ) {
      return [
        value as {
          blocks?: ReportBlock[];
        },
      ];
    }

    if (
      typeof value === 'string'
    ) {
      try {
        const parsed =
          JSON.parse(value);

        return this.normalizeJsonData(
          parsed,
        );
      } catch (error) {
        console.warn(
          'Invalid section jsonData:',
          value,
          error,
        );

        return [];
      }
    }

    return [];
  }

  private normalizeBlockType(
    value: unknown,
  ): ReportBlock['type'] {
    const type =
      String(value ?? '')
        .trim()
        .toLowerCase();

    if (
      type === 'heading' ||
      type === 'paragraph' ||
      type === 'bullet-list' ||
      type === 'numbered-list' ||
      type === 'table'
    ) {
      return type;
    }

    return 'paragraph';
  }

  /* ====================================================== */
  /* SAVE SECTION */
  /* ====================================================== */

  saveSection(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (proposalSequence <= 0) {
      this.errorMessage =
        this.translate.instant('PROPOSAL_REVIEW.PROPOSAL_SEQUENCE_REQUIRED');

      this.reportValidationError(this.errorMessage);
      return;
    }

    if (this.onboardingId <= 0) {
      this.errorMessage =
        this.translate.instant('PROPOSAL_REVIEW.ONBOARDING_SEQUENCE_REQUIRED');

      this.reportValidationError(this.errorMessage);
      return;
    }

    if (Number(this.refValSeq) <= 0) {
      this.errorMessage =
        this.translate.instant('PROPOSAL_REVIEW.SECTION_REFERENCE_REQUIRED');

      this.reportValidationError(this.errorMessage);
      return;
    }

    const cleanedBlocks =
      this.blocks.map(
        (
          block:
            ReportBlock,
        ) => ({
          type:
            block.type,

          title:
            String(
              block.title ?? '',
            ).trim(),

          content:
            block.type === 'paragraph'
              ? richTextForSave(block.content)
              : String(
                  block.content ?? '',
                ),

          delFlg:
            Number(
              block.delFlg ?? 0,
            ),

          table:
            block.type === 'table'
              ? this.normalizeTable(
                  block.table,
                )
              : null,
        }),
      );

    const payload = {
      crprtOnbrdngSeq:
        this.onboardingId,

      crdtPrpslSeq:
        proposalSequence,

      blockList: [
        {
          crdtPrpslBlkSeq:
            this.crdtPrpslBlkSeq,

          refGrp:
            Number(this.refGrp),

          refValSeq:
            Number(this.refValSeq),

          blkTyp:
            3,

          delFlg:
            0,

          sortOrdr:
            1,

          txtData:
            '',

          jsonData: [
            {
              blocks:
                cleanedBlocks,
            },
          ],
        },
      ],
    };

    console.log(
      'Rich section save payload:',
      payload,
    );

    this.saving = true;

    this.creditService
      .saveSection(payload)
      .subscribe({
        next: (response: any) => {
          this.saving = false;

          const returnedProposalSequence =
            Number(
              response?.crdtPrpslSeq ??
              response?.data?.crdtPrpslSeq ??
              proposalSequence,
            );

          if (
            returnedProposalSequence >
            0
          ) {
            this.crdtPrpslSeq =
              returnedProposalSequence;

            this.creditService
              .setCrdtPrpslSeq(
                returnedProposalSequence,
              );
          }

          const returnedBlockSequence =
            this.toNullableNumber(
              response?.crdtPrpslBlkSeq ??
              response?.data
                ?.crdtPrpslBlkSeq,
            );

          if (
            returnedBlockSequence !==
            null
          ) {
            this.crdtPrpslBlkSeq =
              returnedBlockSequence;
          }

          this.successMessage =
            this.translate.instant('PROPOSAL_REVIEW.SAVE_SUCCESS');

          this.toast.success(this.successMessage);

          this.loadSection();

          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'saved',
          });
        },

        error: (error) => {
          this.saving = false;

          this.errorMessage =
            error?.error?.message ??
            this.translate.instant('PROPOSAL_REVIEW.SAVE_FAILED');

          console.error(
            'Save rich section error:',
            error,
          );

          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'api-error',
            message: this.errorMessage,
          });
        },
      });
  }

  private reportValidationError(message: string): void {
    this.creditService.reportSectionSaveResult({
      sectionId: this.sectionId,
      status: 'validation-error',
      message,
    });
  }

  /* ====================================================== */
  /* ADD BLOCKS */
  /* ====================================================== */

  addHeading(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'heading',
        title: '',
        content: '',
        expanded: true,
        delFlg: 0,
      },
    ];
  }

  addParagraph(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'paragraph',
        title: '',
        content: '',
        expanded: true,
        delFlg: 0,
      },
    ];
  }

  addBulletList(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'bullet-list',
        title: '',
        content: '',
        expanded: true,
        delFlg: 0,
      },
    ];
  }

  addNumberedList(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'numbered-list',
        title: '',
        content: '',
        expanded: true,
        delFlg: 0,
      },
    ];
  }

  addTable(): void {
    this.blocks = [
      ...this.blocks,
      {
        type: 'table',
        title: '',
        content: '',
        expanded: true,
        delFlg: 0,

        table: {
          columns: [],
          rows: [],
        },
      },
    ];
  }

  requestRemoveBlock(
    activeIndex: number,
  ): void {
    const selectedBlock =
      this.activeBlocks[
        activeIndex
      ];

    if (!selectedBlock) {
      return;
    }

    this.pendingDeleteIndex = activeIndex;
    this.deleteMessage = `PROPOSAL_REVIEW.DELETE_CONFIRM.${String(selectedBlock.type).toUpperCase().replace(/-/g, '_')}`;
    this.showDeletePopup = true;
  }

  confirmRemoveBlock(): void {
    if (this.pendingDeleteIndex === null) {
      return;
    }

    this.removeBlock(this.pendingDeleteIndex);
    this.pendingDeleteIndex = null;

    this.toast.success('PROPOSAL_REVIEW.BLOCK_DELETED');
  }

  /** Drag-and-drop reorder; blocks are saved in array order. */
  onBlockDrop(event: CdkDragDrop<unknown>): void {
    this.blocks = moveVisibleItem(
      this.blocks,
      (block) => Number(block.delFlg ?? 0) !== 1,
      event.previousIndex,
      event.currentIndex,
    );
  }

  private removeBlock(
    activeIndex: number,
  ): void {
    const selectedBlock =
      this.activeBlocks[
        activeIndex
      ];

    if (!selectedBlock) {
      return;
    }

    selectedBlock.delFlg = 1;
    selectedBlock.expanded = false;

    this.blocks = [
      ...this.blocks,
    ];
  }

  /* ====================================================== */
  /* TABLE HELPERS */
  /* ====================================================== */

  private ensureTable(
    block: ReportBlock,
  ): ReportTable {
    if (!block.table) {
      block.table = {
        columns: [],
        rows: [],
      };
    }

    return block.table;
  }

  private normalizeTable(
    table: unknown,
  ): ReportTable {
    const source =
      table as {
        columns?: unknown;
        rows?: unknown;
      };

    const rawColumns =
      Array.isArray(
        source?.columns,
      )
        ? source.columns
        : [];

    const columns:
      ReportTableColumn[] =
      rawColumns.map(
        (column: unknown) => {
          if (
            typeof column ===
            'string'
          ) {
            return {
              title:
                column,

              subColumns:
                [],
            };
          }

          const value =
            column as {
              title?: unknown;
              subColumns?: unknown;
            };

          return {
            title:
              typeof value?.title ===
              'string'
                ? value.title
                : '',

            subColumns:
              Array.isArray(
                value?.subColumns,
              )
                ? value.subColumns.map(
                    (subColumn) =>
                      String(
                        subColumn ?? '',
                      ),
                  )
                : [],
          };
        },
      );

    const leafColumnCount =
      columns.reduce(
        (
          total:
            number,
          column:
            ReportTableColumn,
        ) =>
          total +
          this.getColumnLeafCount(
            column,
          ),
        0,
      );

    const rawRows =
      Array.isArray(
        source?.rows,
      )
        ? source.rows
        : [];

    const rows:
      string[][] =
      rawRows.map(
        (row: unknown) => {
          const values =
            Array.isArray(row)
              ? row.map(
                  (cell) =>
                    String(
                      cell ?? '',
                    ),
                )
              : [];

          while (
            values.length <
            leafColumnCount
          ) {
            values.push('');
          }

          return values.slice(
            0,
            leafColumnCount,
          );
        },
      );

    return {
      columns,
      rows,
    };
  }

  getColumnLeafCount(
    column: ReportTableColumn,
  ): number {
    return (
      column.subColumns
        ?.length || 1
    );
  }

  getLeafColumns(
    block: ReportBlock,
  ): Array<{
    headerIndex: number;
    subIndex: number | null;
  }> {
    const leafColumns:
      Array<{
        headerIndex: number;
        subIndex: number | null;
      }> = [];

    if (!block.table) {
      return leafColumns;
    }

    block.table.columns.forEach(
      (
        header:
          ReportTableColumn,
        headerIndex:
          number,
      ) => {
        if (
          header.subColumns
            .length > 0
        ) {
          header.subColumns.forEach(
            (
              _subColumn,
              subIndex,
            ) => {
              leafColumns.push({
                headerIndex,
                subIndex,
              });
            },
          );

          return;
        }

        leafColumns.push({
          headerIndex,
          subIndex: null,
        });
      },
    );

    return leafColumns;
  }

  hasAnySubColumns(
    block: ReportBlock,
  ): boolean {
    return Boolean(
      block.table
        ?.columns.some(
          (
            header:
              ReportTableColumn,
          ) =>
            header.subColumns
              .length > 0,
        ),
    );
  }

  getHeaderRowSpan(
    block: ReportBlock,
    header: ReportTableColumn,
  ): number {
    return (
      this.hasAnySubColumns(
        block,
      ) &&
      header.subColumns
        .length === 0
    )
      ? 2
      : 1;
  }

  private getHeaderStartIndex(
    block: ReportBlock,
    headerIndex: number,
  ): number {
    if (!block.table) {
      return 0;
    }

    return block.table.columns
      .slice(
        0,
        headerIndex,
      )
      .reduce(
        (
          total:
            number,
          header:
            ReportTableColumn,
        ) =>
          total +
          this.getColumnLeafCount(
            header,
          ),
        0,
      );
  }

  addHeader(
    block: ReportBlock,
  ): void {
    const table =
      this.ensureTable(
        block,
      );

    table.columns.push({
      title: '',
      subColumns: [],
    });

    table.rows.forEach(
      (row: string[]) => {
        row.push('');
      },
    );
  }

  removeHeader(
    block: ReportBlock,
    headerIndex: number,
  ): void {
    if (!block.table) {
      return;
    }

    const header =
      block.table.columns[
        headerIndex
      ];

    if (!header) {
      return;
    }

    const startIndex =
      this.getHeaderStartIndex(
        block,
        headerIndex,
      );

    const removeCount =
      this.getColumnLeafCount(
        header,
      );

    block.table.columns.splice(
      headerIndex,
      1,
    );

    block.table.rows.forEach(
      (row: string[]) => {
        row.splice(
          startIndex,
          removeCount,
        );
      },
    );
  }

  addSubColumn(
    block: ReportBlock,
    headerIndex: number,
  ): void {
    if (!block.table) {
      return;
    }

    const header =
      block.table.columns[
        headerIndex
      ];

    if (!header) {
      return;
    }

    /*
     * First sub-column uses the
     * existing data column.
     */
    if (
      header.subColumns
        .length === 0
    ) {
      header.subColumns.push('');

      return;
    }

    const insertAt =
      this.getHeaderStartIndex(
        block,
        headerIndex,
      ) +
      header.subColumns.length;

    header.subColumns.push('');

    block.table.rows.forEach(
      (row: string[]) => {
        row.splice(
          insertAt,
          0,
          '',
        );
      },
    );
  }

  removeSubColumn(
    block: ReportBlock,
    headerIndex: number,
    subIndex: number,
  ): void {
    if (!block.table) {
      return;
    }

    const header =
      block.table.columns[
        headerIndex
      ];

    if (
      !header ||
      header.subColumns
        .length === 0
    ) {
      return;
    }

    /*
     * Returning to a normal column
     * keeps the existing data cell.
     */
    if (
      header.subColumns
        .length === 1
    ) {
      header.subColumns = [];

      return;
    }

    const removeAt =
      this.getHeaderStartIndex(
        block,
        headerIndex,
      ) +
      subIndex;

    header.subColumns.splice(
      subIndex,
      1,
    );

    block.table.rows.forEach(
      (row: string[]) => {
        row.splice(
          removeAt,
          1,
        );
      },
    );
  }

  addRow(
    block: ReportBlock,
  ): void {
    const table =
      this.ensureTable(
        block,
      );

    const columnCount =
      this.getLeafColumns(
        block,
      ).length;

    if (
      columnCount <= 0
    ) {
      return;
    }

    table.rows.push(
      Array.from(
        {
          length:
            columnCount,
        },
        () => '',
      ),
    );
  }

  removeRow(
    block: ReportBlock,
    rowIndex: number,
  ): void {
    block.table?.rows.splice(
      rowIndex,
      1,
    );
  }

  /* ====================================================== */
  /* CONTENT HELPERS */
  /* ====================================================== */

  getLines(
    content = '',
  ): string[] {
    return String(content)
      .split('\n')
      .map((item) =>
        item.trim(),
      )
      .filter(Boolean);
  }

  private toNullableNumber(
    value: unknown,
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numericValue =
      Number(value);

    return Number.isFinite(
      numericValue,
    )
      ? numericValue
      : null;
  }

  private getOnboardingSequence():
    number {
    let currentRoute:
      ActivatedRoute | null =
      this.route;

    while (currentRoute) {
      const routeValue =
        currentRoute.snapshot
          .paramMap.get('id') ??
        currentRoute.snapshot
          .paramMap.get('oid');

      const sequence =
        Number(routeValue);

      if (
        routeValue &&
        Number.isFinite(sequence) &&
        sequence > 0
      ) {
        return sequence;
      }

      currentRoute =
        currentRoute.parent;
    }

    return 0;
  }
}